import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const emptyDevotion = {
  country_cd: 'KR',
  language: 'ko',
  devotions_date: '',
  title: '',
  hymn: '',
  grace_sharing_text: '',
  scripture_reference: '',
  scripture_text: '',
  commentary_text: '',
  lets_get_it_text: '',
  etc1: '',
  closing_text: '',
};

const FamilyWorshipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [devotion, setDevotion] = useState(emptyDevotion);
  const [questions, setQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!isNew) {
      loadDevotion(id);
    } else {
      setDevotion(emptyDevotion);
      setQuestions([]);
      setDeletedQuestionIds([]);
      setLoading(false);
    }
  }, [id, isNew]);

  const loadDevotion = async (devotionId) => {
    setLoading(true);
    setMessage(null);
    const [{ data: devotionData, error: devotionError }, { data: questionData, error: questionError }] =
      await Promise.all([
        supabase
          .from('family_devotions')
          .select(
            'id,country_cd,language,devotions_date,title,hymn,grace_sharing_text,scripture_reference,scripture_text,commentary_text,lets_get_it_text,etc1,closing_text,is_active,created_at,updated_at'
          )
          .eq('id', devotionId)
          .single(),
        supabase.from('family_devotions_q').select('id,devotions_id,country_cd,seq,question,reference,closing_text,answer_text,created_at,updated_at').eq('devotions_id', devotionId).order('seq', { ascending: true }),
      ]);

    if (devotionError) {
      setMessage({ type: 'error', text: devotionError.message });
      setLoading(false);
      return;
    }

    setDevotion({
      ...devotionData,
      language: devotionData.language || (devotionData.country_cd === 'US' ? 'en' : 'ko'),
      devotions_date: toInputDate(devotionData.devotions_date),
    });

    setQuestions(questionData || []);
    setLoading(false);
  };

  const handleDevotionChange = (e) => {
    const { name, value } = e.target;
    setDevotion((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        tempId: createTempId(),
        devotions_id: devotion.id,
        country_cd: devotion.country_cd,
        seq: prev.length + 1,
        question: '',
        reference: '',
        closing_text: '',
        answer_text: '',        
      },
    ]);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => {
      const target = prev[index];
      if (target?.id) {
        setDeletedQuestionIds((ids) => [...ids, target.id]);
      }
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const devotionPayload = useMemo(
    () => ({
      country_cd: devotion.country_cd,
      language: (devotion.language && String(devotion.language).trim()) || (devotion.country_cd === 'US' ? 'en' : 'ko'),
      title: devotion.title.trim(),
      hymn: devotion.hymn?.trim() || null,
      grace_sharing_text: devotion.grace_sharing_text?.trim() || null,
      scripture_reference: devotion.scripture_reference.trim(),
      scripture_text: devotion.scripture_text.trim(),
      commentary_text: devotion.commentary_text?.trim() || null,
      lets_get_it_text: devotion.lets_get_it_text?.trim() || null,
      etc1: devotion.etc1?.trim() || null,
      closing_text: devotion.closing_text?.trim() || null,
      devotions_date: toDbDate(devotion.devotions_date),
    }),
    [devotion]
  );

  const validateDevotion = () => {
    if (!devotionPayload.title || !devotionPayload.scripture_reference || !devotionPayload.scripture_text || !devotionPayload.devotions_date || !devotionPayload.country_cd) {
      setMessage({ type: 'error', text: '필수 항목(국가, 진행일, 제목, 말씀 본문, 본문 내용)을 입력하세요.' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateDevotion()) return;
    setSaving(true);
    setMessage(null);
    try {
      let devotionId = devotion.id;
      if (isNew || !devotionId) {
        const { data, error } = await supabase.from('family_devotions').insert(devotionPayload).select().single();
        if (error) throw error;
        devotionId = data.id;
        setDevotion((prev) => ({ ...prev, id: devotionId, devotions_date: toInputDate(data.devotions_date) }));
        navigate(`/family-worship/${devotionId}`, { replace: true });
      } else {
        const { error } = await supabase.from('family_devotions').update(devotionPayload).eq('id', devotionId);
        if (error) throw error;
      }

      await syncQuestions(devotionId);
      setMessage({ type: 'success', text: '저장되었습니다.' });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: error.message || '저장 중 오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const syncQuestions = async (devotionId) => {
    if (!devotionId) return;

    const preparedQuestions = questions.map((question, index) => ({
      id: question.id,
      devotions_id: devotionId,
      country_cd: devotion.country_cd,
      seq: Number(question.seq) || index + 1,
      question: question.question?.trim(),
      reference: question.reference?.trim() || null,
      closing_text: question.closing_text?.trim() || null,
      answer_text: question.answer_text?.trim() || null,
    }));

    const newQuestions = preparedQuestions.filter((q) => !q.id && q.question);
    const existingQuestions = preparedQuestions.filter((q) => q.id);

    if (newQuestions.length > 0) {
      const questionsToInsert = newQuestions.map(({ id, ...rest }) => rest);
      const { data, error } = await supabase.from('family_devotions_q').insert(questionsToInsert).select();
      if (error) throw error;
      setQuestions((prev) => {
        const updated = [...prev];
        newQuestions.forEach((newQuestion, idx) => {
          const dataMatch = data[idx];
          const targetIndex = updated.findIndex(
            (item) => !item.id && item.question?.trim() === newQuestion.question && item.seq === newQuestion.seq
          );
          if (targetIndex !== -1) {
            updated[targetIndex] = dataMatch;
          }
        });
        return updated;
      });
    }

    if (existingQuestions.length > 0) {
      const { error } = await supabase.from('family_devotions_q').upsert(existingQuestions, { onConflict: 'id' });
      if (error) throw error;
    }

    if (deletedQuestionIds.length > 0) {
      const { error } = await supabase.from('family_devotions_q').delete().in('id', deletedQuestionIds);
      if (error) throw error;
      setDeletedQuestionIds([]);
    }
  };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">{isNew ? '가정예배 등록' : '가정예배 상세'}</h6>
        <div>
          <button className="btn btn-outline-secondary btn-sm mr-2" onClick={() => navigate('/family-worship')}>
            목록으로
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
      <div className="card-body">
        {message && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>{message.text}</div>
        )}
        <form>
          <div className="form-row">
            <div className="form-group col-md-2">
              <label>국가</label>
              <select name="country_cd" className="form-control" value={devotion.country_cd} onChange={handleDevotionChange}>
                  <option value="KR">한국어</option>
                  <option value="US">영어</option>
              </select>
            </div>
            <div className="form-group col-md-2">
              <label>언어 (language)</label>
              <select name="language" className="form-control" value={devotion.language || 'ko'} onChange={handleDevotionChange}>
                <option value="ko">ko</option>
                <option value="en">en</option>
                <option value="ja">ja</option>
                <option value="zh">zh</option>
              </select>
            </div>
            <div className="form-group col-md-3">
              <label>진행일</label>
              <input
                type="date"
                className="form-control"
                name="devotions_date"
                value={devotion.devotions_date}
                onChange={handleDevotionChange}
                required
              />
            </div>
            <div className="form-group col-md-5">
              <label>찬송</label>
            <input type="text" className="form-control" name="hymn" value={devotion.hymn || ''} onChange={handleDevotionChange} />
            </div>
          </div>          
          <div className="form-row">
            <div className="col-md-6">
              <div className="form-group">
                <label>제목</label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={devotion.title}
                  onChange={handleDevotionChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>한주의 삶을 나눠요</label>
                <textarea
                  className="form-control"
                  name="grace_sharing_text"
                  rows="2"
                  value={devotion.grace_sharing_text || ''}
                  onChange={handleDevotionChange}
                />
              </div>
                <div className="form-group">
                  <label>말씀을 읽어요</label>
                  <input
                    type="text"
                    className="form-control"
                    name="scripture_reference"
                    value={devotion.scripture_reference}
                    onChange={handleDevotionChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>본문 내용</label>
                  <textarea
                    className="form-control"
                    name="scripture_text"
                    rows="10
                    "
                    value={devotion.scripture_text}
                    onChange={handleDevotionChange}
                    required
                  />
                </div>
              
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>말씀을 이해해요</label>
                <textarea
                  className="form-control"
                  name="commentary_text"
                  rows="6"
                  value={devotion.commentary_text || ''}
                  onChange={handleDevotionChange}
                />
              </div>
              <div className="form-group">
                <label>Let's get it text</label>
                <textarea
                  className="form-control"
                  name="lets_get_it_text"
                  rows="3"
                  value={devotion.lets_get_it_text || ''}
                  onChange={handleDevotionChange}
                />
              </div>
              <div className="form-group">
                <label>마무리 기도</label>
                <textarea
                  className="form-control"
                  name="closing_text"
                  rows="3"
                  value={devotion.closing_text || ''}
                  onChange={handleDevotionChange}
                />
              </div>
              <div className="form-group">
                <label>정답</label>
                <textarea
                  className="form-control"
                  name="etc1"
                  rows="2"
                  value={devotion.etc1 || ''}
                  onChange={handleDevotionChange}
                />
              </div>
            </div>
          </div>
        </form>

        <hr />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="font-weight-bold mb-0">말씀을 관찰해요</h6>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddQuestion}>
            질문 추가
          </button>
        </div>

        {questions.length === 0 && <div className="text-muted mb-3">등록된 질문이 없습니다.</div>}

        {questions.map((question, index) => (
          <div className="card mb-3" key={question.id || question.tempId || `new-${index}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>질문 #{question.seq || index + 1}</strong>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveQuestion(index)}
                >
                  삭제
                </button>
              </div>
              <div className="form-row">
                <div className="form-group col-md-2">
                  <label>순번</label>
                  <input
                    type="number"
                    className="form-control"
                    value={question.seq || index + 1}
                    onChange={(e) => handleQuestionChange(index, 'seq', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-10">
                  <label>질문</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.question || ''}
                    onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>답변</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={question.answer_text || ''}
                  onChange={(e) => handleQuestionChange(index, 'answer_text', e.target.value)}
                />
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>참고/본문</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.reference || ''}
                    onChange={(e) => handleQuestionChange(index, 'reference', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>마무리 문구</label>
                  <input
                    type="text"
                    className="form-control"
                    value={question.closing_text || ''}
                    onChange={(e) => handleQuestionChange(index, 'closing_text', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const toInputDate = (value) => {
  if (!value) return '';
  if (value.includes('-')) return value;
  if (value.length === 8) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  return value;
};

const toDbDate = (value) => {
  if (!value) return null;
  if (!value.includes('-')) return value;
  return value.replace(/-/g, '');
};

const createTempId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default FamilyWorshipDetail;
